/**
 * Weapons online (docs/WEAPONS.md §13): the wire table, the item ids behind each weapon and the
 * server's PvP weapon rules. Pure (shared by the server and the client UI / renderer).
 *
 * The weapon numbers (reach, arc, range, spread, rate, capacity, reload) come from the SAME roster
 * the offline game plays (`src/weapons/weaponData.ts`), like vehicle limits derive from the vehicle
 * specs, so the server judges exactly the gun the client fires. Only PvP damage is scaled here:
 * players have 100 health, respawn, and a fight should take more than one blow.
 */
import { damageAt } from '../src/weapons/combat';
import { WEAPONS, WEAPON_ORDER, type FirearmSpec, type WeaponId } from '../src/weapons/weaponData';

export type { WeaponId };

/** Wire ids (snapshot flags bits 21..23, `wpn*` messages): the index into this table. 0 = fists. */
export const WEAPON_WIRE: readonly WeaponId[] = WEAPON_ORDER;

export const weaponWireId = (id: WeaponId): number => Math.max(0, WEAPON_WIRE.indexOf(id));
/** Weapon for a wire id (unknown ids — a newer server — read as fists). */
export const weaponFromWire = (n: number): WeaponId => WEAPON_WIRE[n] ?? 'fists';

/** The inventory item each weapon is online (fists are always on you). */
export const WEAPON_ITEM: Readonly<Record<WeaponId, string | null>> = {
  fists: null,
  cosh: 'wpn-cosh',
  bat: 'wpn-bat',
  crowbar: 'wpn-crowbar',
  knife: 'wpn-knife',
  revolver: 'wpn-revolver',
  sawnoff: 'wpn-sawnoff',
};

/** The rounds item each gun eats. */
export const AMMO_ITEM: Readonly<Partial<Record<WeaponId, string>>> = { revolver: 'ammo-38', sawnoff: 'ammo-12' };

/** Weapon an item def IS (`wpn-*`), or null. */
export function weaponOfItem(defId: string): WeaponId | null {
  for (const id of WEAPON_WIRE) if (WEAPON_ITEM[id] === defId) return id;
  return null;
}

/** Gun an ammo item def feeds, or null. */
export function gunOfAmmo(defId: string): WeaponId | null {
  for (const id of WEAPON_WIRE) if (AMMO_ITEM[id] === defId) return id;
  return null;
}

/** PvP tuning (server rules; the client uses them only to word the UI and pick targets). */
export const PVP_WEAPONS = {
  /** A bare-fisted blow (the old `pvpHit` melee number). */
  fistDamage: 12,
  /** Weapon damage against players, relative to the offline roster's (people there have 100 too). */
  damageScale: 0.5,
  /**
   * No single shot does more than this to a player (WP-N3): the sawn-off's both-pellets-in point-blank blast
   * (9 × 24 × 0.5 = 108) would otherwise drop a full-health player in one. Two shots still do it. Online PvP
   * only: offline numbers and shots at officers are untouched.
   */
  maxShotDamage: 70,
  /** Body radius of a player for reach and pellet maths (m). */
  targetRadius: 0.5,
  /** Melee: extra reach on top of the weapon's (latency, a step in) (m). */
  meleeReachSlack: 0.9,
  /** Melee: extra arc on top of the weapon's (rad). */
  meleeArcSlack: 0.45,
  /** Melee: this close, any direction counts (you can't miss someone you're touching) (m). */
  closeContact: 0.9,
  /** Guns: extra angle on top of the spread cone for a claimed hit (aim latency) (rad). */
  aimSlack: 0.1,
  /** Guns: extra range for latency (m). */
  rangeSlack: 1.5,
  /** Hip fire and running widen the cone (as offline, weaponSystem.ts). */
  hipSpread: 1.8,
  movingSpread: 1.3,
  movingSpeed: 3,
  /** The claimed muzzle must be this close to the shooter's server position (m): no teleport-shots. */
  originSlack: 2.5,
  /** Lag compensation: targets are rewound at most this far into the past (ms). */
  maxRewindMs: 350,
  /** Fire-rate leeway for bunched packets (ms). */
  fireLeewayMs: 120,
  /** A reload is done after this share of the weapon's reload time (latency). */
  reloadTolerance: 0.85,
  /** Somebody sitting in a car is shot through the glass and the door: this share of the damage. */
  inVehicleDamage: 0.5,
  /** A gun going off is reported to the police at most this often (one burst = one incident) (ms). */
  dischargeGapMs: 2500,
} as const;

/** Server damage of a landed blow with `w` (fists keep the old punch number). */
export function pvpMeleeDamage(w: WeaponId): number {
  if (w === 'fists') return PVP_WEAPONS.fistDamage;
  return Math.max(1, Math.round(WEAPONS[w].melee.damage * PVP_WEAPONS.damageScale));
}

/**
 * How many of a shot's projectiles land on a body `off` radians off the aim line, `dist` metres away.
 * Pellets are spread evenly across the cone (as offline, `pelletHeadings` without the jitter), so the
 * count is deterministic: the server never takes the client's word for it. A single round lands when
 * the body is inside the cone (plus aim slack); a shotgun counts the pellets whose line crosses the
 * body, at least one when the body is inside the cone at all.
 */
export function pelletsOnTarget(spec: FirearmSpec, off: number, dist: number, spreadScale = 1): number {
  const body = Math.atan2(PVP_WEAPONS.targetRadius, Math.max(0.3, dist));
  const s = spec.spread * spreadScale;
  const a = Math.abs(off);
  if (a > s + body + PVP_WEAPONS.aimSlack) return 0;
  if (spec.pellets <= 1) return 1;
  let n = 0;
  for (let i = 0; i < spec.pellets; i++) {
    const even = -1 + (2 * i) / (spec.pellets - 1);
    if (Math.abs(even * s - off) <= body + 0.02) n++;
  }
  return Math.max(1, n);
}

/** Server damage of a shot: pellets × the roster's damage at that range × the PvP scale, at most `maxShotDamage`. */
export function pvpShotDamage(spec: FirearmSpec, pellets: number, dist: number): number {
  if (pellets <= 0) return 0;
  return Math.min(PVP_WEAPONS.maxShotDamage, Math.max(1, Math.round(pellets * damageAt(spec, dist) * PVP_WEAPONS.damageScale)));
}
